import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, User, Car } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import AppointmentStatusUpdateContainer from './AppointmentStatusUpdateContainer';

const CalendarContainer = styled.div`
  background: #25253C;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    border-radius: 8px;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const MonthYear = styled.h2`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 768px) {
     font-size: 18px;
  }
  
  @media (max-width: 480px) {
     font-size: 16px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #2A2A2A;
  background: transparent;
  color: #9E9E9E;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #F43F60;
    color: #F43F60;
    background: rgba(244, 63, 96, 0.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const WeekDaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 2px;
    margin-bottom: 8px;
  }
`;

const WeekDay = styled.div`
  color: #9E9E9E;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  padding: 8px 4px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 2px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 2px 1px;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 2px;
  }
  
  /* Ensure all cells have the same size */
  > * {
    width: 100%;
    height: 100%;
  }
`;

const DayCell = styled.div`
  aspect-ratio: 1;
  background: ${props => props.isCurrentMonth ? '#1E1E35' : '#313151'};
  border: 1px solid #2A2A2A;
  border-radius: 8px;
  padding: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  
  @media (max-width: 768px) {
    padding: 6px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 2px;
    border-radius: 4px;
    aspect-ratio: auto;
    min-height: 60px;
  }
`;

const DayNumber = styled.div`
  color: ${props => props.isCurrentMonth ? '#ededed' : '#666'};
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 3px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    margin-bottom: 1px;
  }
`;

const AppointmentCard = styled.div`
  background: ${props => {
    switch (props.status) {
      case 'completed': return 'rgba(34, 197, 94, 0.15)';
      case 'cancelled': return 'rgba(239, 68, 68, 0.15)';
      default: return 'rgba(244, 63, 96, 0.15)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'completed': return 'rgba(34, 197, 94, 0.3)';
      case 'cancelled': return 'rgba(239, 68, 68, 0.3)';
      default: return 'rgba(244, 63, 96, 0.3)';
    }
  }};
  border-radius: 6px;
  padding: 6px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    background: ${props => {
      switch (props.status) {
        case 'completed': return 'rgba(34, 197, 94, 0.25)';
        case 'cancelled': return 'rgba(239, 68, 68, 0.25)';
        default: return 'rgba(244, 63, 96, 0.25)';
      }
    }};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 4px;
    margin-bottom: 3px;
    border-radius: 4px;
  }
  
  @media (max-width: 480px) {
    padding: 2px;
    margin-bottom: 1px;
    border-radius: 2px;
  }
`;

const AppointmentTitle = styled.div`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 1px;
  }
`;

const AppointmentTime = styled.div`
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 2px;

`;

const AppointmentCustomer = styled.div`
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

`;

const MoreAppointments = styled.div`
  color: #F43F60;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  padding: 2px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.55rem;
    padding: 1px;
  }
`;

const AppointmentsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(244, 63, 96, 0.5);
    border-radius: 2px;
    
    &:hover {
      background: rgba(244, 63, 96, 0.7);
    }
  }
  
  @media (max-width: 480px) {
    /* Hide scrollbar on mobile for cleaner look */
    &::-webkit-scrollbar {
      width: 0;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #9E9E9E;
  padding: 40px 20px;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    padding: 32px 16px;
    
    svg {
      width: 40px;
      height: 40px;
      margin-bottom: 12px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 24px 12px;
    
    svg {
      width: 32px;
      height: 32px;
      margin-bottom: 8px;
    }
  }
`;

const AppointmentsCalendar = ({ appointments = [], onAppointmentClick }) => {
    const [currentDate, setCurrentDate] = useState(new Date());

    const calendarDays = useMemo(() => {
        const start = startOfMonth(currentDate);
        const end = endOfMonth(currentDate);
        const days = eachDayOfInterval({ start, end });
        
        // Add padding days to fill the grid
        const firstDayOfWeek = start.getDay();
        const lastDayOfWeek = end.getDay();
        
        const paddingStart = Array.from({ length: firstDayOfWeek }, (_, i) => {
            const date = new Date(start);
            date.setDate(date.getDate() - (firstDayOfWeek - i));
            return date;
        });
        
        const paddingEnd = Array.from({ length: 6 - lastDayOfWeek }, (_, i) => {
            const date = new Date(end);
            date.setDate(date.getDate() + (i + 1));
            return date;
        });
        
        return [...paddingStart, ...days, ...paddingEnd];
    }, [currentDate]);

    const appointmentsByDate = useMemo(() => {
        const grouped = {};
        appointments.forEach(appointment => {
            const dateKey = format(new Date(appointment.date), 'yyyy-MM-dd');
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(appointment);
        });
        return grouped;
    }, [appointments]);

    const handlePreviousMonth = () => {
        setCurrentDate(subMonths(currentDate, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(addMonths(currentDate, 1));
    };

    const handleAppointmentClick = (appointment, e) => {
        e.stopPropagation();
        if (onAppointmentClick) {
            onAppointmentClick(appointment);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#22C55E';
            case 'cancelled': return '#EF4444';
            default: return '#F43F60';
        }
    };

    if (appointments.length === 0) {
        return (
            <EmptyState>
                <CalendarIcon />
                <div>No appointments found for the selected date range</div>
            </EmptyState>
        );
    }

    return (
        <CalendarContainer>
            <CalendarHeader>
                <MonthYear>{format(currentDate, 'MMMM yyyy')}</MonthYear>
                <NavigationButtons>
                    <NavButton onClick={handlePreviousMonth} title="Previous Month">
                        <ChevronLeft />
                    </NavButton>
                    <NavButton onClick={handleNextMonth} title="Next Month">
                        <ChevronRight />
                    </NavButton>
                </NavigationButtons>
            </CalendarHeader>
            
            <WeekDaysHeader>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <WeekDay key={day}>{day}</WeekDay>
                ))}
            </WeekDaysHeader>
            
            <CalendarGrid>
                {calendarDays.map((day, index) => {
                    const dateKey = format(day, 'yyyy-MM-dd');
                    const dayAppointments = appointmentsByDate[dateKey] || [];
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());
                    
                    return (
                        <DayCell key={index} isCurrentMonth={isCurrentMonth}>
                            <DayNumber 
                                isCurrentMonth={isCurrentMonth}
                                style={isToday ? { color: '#F43F60', fontWeight: '700' } : {}}
                            >
                                {format(day, 'd')}
                            </DayNumber>
                            
                            <AppointmentsContainer>
                            {dayAppointments.slice(0, 3).map((appointment, idx) => (
                                <AppointmentCard 
                                    key={appointment.id || idx}
                                    status={appointment.status}
                                    onClick={(e) => handleAppointmentClick(appointment, e)}
                                    title="Click to view details"
                                >
                                    <AppointmentTitle>
                                        {appointment.offer?.vehicle?.year} {appointment.offer?.vehicle?.make} {appointment.offer?.vehicle?.model}
                                    </AppointmentTitle>
                                    <AppointmentTime>
                                        <Clock size={10} />
                                        {appointment.timeSlot?.start || '--'}
                                    </AppointmentTime>
                                    <AppointmentCustomer>
                                        <User size={10} />
                                        {appointment.offer?.user?.userInfo?.name || '--'}
                                    </AppointmentCustomer>
                                </AppointmentCard>
                            ))}
                            
                            {dayAppointments.length > 3 && (
                                <MoreAppointments>
                                    +{dayAppointments.length - 3} more
                                </MoreAppointments>
                            )}
                            </AppointmentsContainer>
                        </DayCell>
                    );
                })}
            </CalendarGrid>
        </CalendarContainer>
    );
};

export default AppointmentsCalendar; 